import { DateRange } from '@blueprintjs/datetime';
import { DateRangeField, useSetInitialDateRange, useUrlDateRangeState } from 'app/components/date-range-field';
import { getLocalDate } from 'app/helpers/getLocalDate';
import { useAppSelector } from 'app/redux/store';
import { useEventsListingParams } from 'app/views/alerts/components/events-listing/useEventsListingParams';
import { DATA_TRACKING_KEY } from 'app/views/components/heap-analytics';
import { useI18n } from 'core/hooks/useI18n';
import { DateTime } from 'luxon';
import { useCallback } from 'react';
import { styled } from 'styled-components';

const FIELD_ID = 'event-listing-time';

const Validation = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.feedback.error};
  font-size: 1.4rem;
`;

export const EventHistoryDateRangeFilter = () => {
  const { l10n } = useI18n('app/views/alerts', 'eventsListing');
  const { setSearchParams, clearSearchParams } = useEventsListingParams();
  const onDateRangeSave = useCallback(
    ({ newState: { from, to } }: { newState: { from: string[]; to: string[] } }) => {
      setSearchParams({
        from,
        to,
      });
    },
    [setSearchParams]
  );
  const onDateRangeClear = useCallback(() => {
    clearSearchParams(['from', 'to']);
  }, [clearSearchParams]);
  const state = useUrlDateRangeState({ onSave: onDateRangeSave, onClear: onDateRangeClear });
  const { selectedDateRange } = state;
  const { isBeyondRestrictedDateRange, isDateRangeInFuture } = useDateRangeValidation(selectedDateRange);
  const saveIsDisabled = isBeyondRestrictedDateRange || isDateRangeInFuture;

  useSetInitialDateRange();

  return (
    <DateRangeField
      id={FIELD_ID}
      label={l10n('filter.label.date')}
      state={state}
      dateRangeSelectorProps={{
        footerElement: <FooterElement selectedDateRange={selectedDateRange} />,
        saveButtonProps: {
          disabled: saveIsDisabled,
          'data-testid': 'event-history-date-range-filter-apply-button',
          'data-tracking': DATA_TRACKING_KEY['event-history-filter-date-range-apply'],
        },
        clearButtonProps: {
          'data-tracking': DATA_TRACKING_KEY['event-history-filter-date-range-clear'],
        },
      }}
    />
  );
};

function FooterElement({ selectedDateRange }: { selectedDateRange: DateRange }) {
  const { l10n } = useI18n('app/views/alerts', 'eventsListing');
  const { isBeyondRestrictedDateRange, isDateRangeInFuture } = useDateRangeValidation(selectedDateRange);
  if (isBeyondRestrictedDateRange) {
    return <Validation>{l10n('filter.dateRange.validation.beyond.thirtyDays')}</Validation>;
  }
  if (isDateRangeInFuture) {
    return <Validation>{l10n('filter.dateRange.validation.beyond.now')}</Validation>;
  }
  return null;
}

const useDateRangeValidation = ([min, max]: DateRange) => {
  const { ianaTimezone } = useAppSelector(state => state.profile.facility);

  if (!ianaTimezone || !min || !max) return { isBeyondRestrictedDateRange: false, isDateRangeInFuture: false };

  const diffInDays = (max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24);
  const isBeyondRestrictedDateRange = diffInDays > 30;

  const now = getLocalDate(DateTime.now().setZone(ianaTimezone));
  const isDateRangeInFuture = min > now || max > now;

  return { isBeyondRestrictedDateRange, isDateRangeInFuture };
};
